import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useWidgetTokenStore } from '@elfsight/lib-utils';
import { useSettings } from '@wu/widget-core';
import { shuffle } from 'es-toolkit';
import { getMaxSize, getPreviewItems, getUpdatedLogos } from '../helpers';

const LogoContext = createContext({});

export const LogoProvider = ({ children }: PropsWithChildren) => {
  const { items, randomOrder, previewItems } = useSettings();
  const widgetTokenStore = useWidgetTokenStore();
  const [processedLogos, setProcessedLogos] = useState({});

  const hasPhotoUpload = Array.isArray(items)
    ? items.some((item) => item?.file?.data != null)
    : false;
  const areItemsReady = hasPhotoUpload && items?.length > 0;

  const effectiveItems = useMemo(
    () => (areItemsReady ? items : getPreviewItems(previewItems).items),
    [areItemsReady, items, previewItems]
  );

  const logoOrders = useMemo(() => {
    const orders = effectiveItems.map((_, i) => i);

    return randomOrder ? shuffle(orders) : orders;
  }, [randomOrder, effectiveItems.length]);

  useEffect(() => {
    const processLogosWrapper = async () => {
      const widgetToken = widgetTokenStore.get();

      if (!widgetToken) {
        return;
      }

      try {
        const { newProcessedLogos, logosToProcess } = effectiveItems.reduce(
          (settings, item, index) => {
            const { aspectRatio, ...otherSettings } =
              processedLogos[item.id] || {};
            if (item.file?.data?.url === otherSettings.file?.data?.url) {
              settings.newProcessedLogos[item.id] = {
                ...otherSettings,
                ...item,
                aspectRatio
              };
            } else {
              settings.logosToProcess.push({ item, index });
            }

            return settings;
          },
          { newProcessedLogos: {}, logosToProcess: [] }
        );

        if (logosToProcess.length) {
          const itemsToProcess = logosToProcess.map(({ item }) => item);
          const updatedLogos = await getUpdatedLogos(
            itemsToProcess,
            widgetToken
          );

          updatedLogos.forEach((logo, i) => {
            const { index } = logosToProcess[i];
            const item = effectiveItems[index];

            newProcessedLogos[item.id] = {
              ...logo
            };
          });
        }

        setProcessedLogos(newProcessedLogos);
      } catch (e) {
        console.warn(e);
      }
    };

    processLogosWrapper();
  }, [effectiveItems, widgetTokenStore]);

  const value = useMemo(() => {
    const logos = effectiveItems
      .map((item) => processedLogos[item.id])
      .filter(Boolean);
    const maxSize = getMaxSize(logos);

    return logos
      .map((logo, index) => ({
        ...logo,
        maxSize,
        order: logoOrders[index]
      }))
      .sort((a, b) => a.order - b.order);
  }, [effectiveItems, processedLogos, logoOrders]);

  return <LogoContext.Provider value={value}>{children}</LogoContext.Provider>;
};

export const useLogos = () => useContext(LogoContext);
